/**
 * Custom hook for managing office hours state and operations
 *
 * Provides a clean interface for office hours management with
 * built-in validation, error handling, and state management.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  managePracticeOfficeHours,
  OfficeHoursConfig,
  OfficeHoursResult,
} from '../services/officeHours/officeHoursService';
import { OfficeHour, OfficeHoursInput } from '../types/practices/types';
import {
  validateOfficeHoursConfiguration,
  ValidationError,
  ValidationResult,
} from '../utils/officeHoursValidation';

export interface UseOfficeHoursOptions {
  initialOfficeHours?: OfficeHoursInput[];
  initialLunchHours?: {
    start: string;
    end: string;
    startDisplay: string;
    endDisplay: string;
  };
  initialObservesLunchHours?: boolean;
}

export interface UseOfficeHoursReturn {
  // State
  officeHoursInput: OfficeHoursInput[];
  observesLunchHours: boolean;
  lunchHours: {
    start: string;
    end: string;
    startDisplay: string;
    endDisplay: string;
  };
  validation: ValidationResult;
  isSubmitting: boolean;
  lastResult: OfficeHoursResult | null;

  // Actions
  setOfficeHoursInput: (hours: OfficeHoursInput[]) => void;
  setObservesLunchHours: (observes: boolean) => void;
  setLunchHours: (hours: {
    start: string;
    end: string;
    startDisplay: string;
    endDisplay: string;
  }) => void;
  updateDayHours: (dayIndex: number, updates: Partial<OfficeHoursInput>) => void;
  toggleDayClosed: (dayIndex: number) => void;
  resetToDefaults: () => void;

  // Operations
  saveOfficeHours: (practiceId: string, existingHours?: OfficeHour[], isEditMode?: boolean) => Promise<OfficeHoursResult>;
  validateConfiguration: () => ValidationResult;

  // Computed
  hasChanges: boolean;
  isValid: boolean;
  errorMessages: string[];
  warningMessages: string[];
}

const defaultOfficeHours: OfficeHoursInput[] = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
].map(day => ({
  day,
  open: '',
  close: '',
  closed: true,
  openDisplay: '',
  closeDisplay: '',
  openError: false,
  closeError: false
}));

const defaultLunchHours = {
  start: '',
  end: '',
  startDisplay: '',
  endDisplay: ''
};

export const useOfficeHours = (options: UseOfficeHoursOptions = {}): UseOfficeHoursReturn => {
  const {
    initialOfficeHours = defaultOfficeHours,
    initialLunchHours = defaultLunchHours,
    initialObservesLunchHours = false
  } = options;

  // State
  const [officeHoursInput, setOfficeHoursInput] = useState<OfficeHoursInput[]>(initialOfficeHours);
  const [observesLunchHours, setObservesLunchHours] = useState(initialObservesLunchHours);
  const [lunchHours, setLunchHours] = useState(initialLunchHours);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState<OfficeHoursResult | null>(null);

  // Validation
  const validation = useMemo(() => {
    return validateOfficeHoursConfiguration(
      officeHoursInput,
      observesLunchHours,
      lunchHours
    );
  }, [officeHoursInput, observesLunchHours, lunchHours]);

  // Computed values
  const hasChanges = useMemo(() => {
    // Compare with initial state to determine if there are changes
    // Compare observesLunchHours
    if (initialObservesLunchHours !== observesLunchHours) {
      return true;
    }

    // Compare lunch hours
    if (
      initialLunchHours.start !== lunchHours.start ||
      initialLunchHours.end !== lunchHours.end ||
      initialLunchHours.startDisplay !== lunchHours.startDisplay ||
      initialLunchHours.endDisplay !== lunchHours.endDisplay
    ) {
      return true;
    }

    // Compare office hours arrays
    if (initialOfficeHours.length !== officeHoursInput.length) {
      return true;
    }

    for (let i = 0; i < initialOfficeHours.length; i++) {
      const initial = initialOfficeHours[i];
      const current = officeHoursInput[i];

      if (
        initial.day !== current.day ||
        initial.open !== current.open ||
        initial.close !== current.close ||
        initial.closed !== current.closed ||
        initial.openDisplay !== current.openDisplay ||
        initial.closeDisplay !== current.closeDisplay
      ) {
        return true;
      }
    }

    return false;
  }, [officeHoursInput, observesLunchHours, lunchHours, initialOfficeHours, initialObservesLunchHours, initialLunchHours]);

  const isValid = validation.isValid;
  const errorMessages = validation.errors.map(e => e.message);
  const warningMessages = validation.warnings.map(w => w.message);

  // Actions
  const updateDayHours = useCallback((dayIndex: number, updates: Partial<OfficeHoursInput>) => {
    setOfficeHoursInput(prev =>
      prev.map((day, index) =>
        index === dayIndex ? { ...day, ...updates } : day
      )
    );
  }, []);

  const toggleDayClosed = useCallback((dayIndex: number) => {
    setOfficeHoursInput(prev =>
      prev.map((day, index) =>
        index === dayIndex
          ? {
              ...day,
              closed: !day.closed,
              open: day.closed ? day.open : '',
              close: day.closed ? day.close : '',
              openDisplay: day.closed ? day.openDisplay : '',
              closeDisplay: day.closed ? day.closeDisplay : ''
            }
          : day
      )
    );
  }, []);

  const resetToDefaults = useCallback(() => {
    setOfficeHoursInput(defaultOfficeHours);
    setObservesLunchHours(false);
    setLunchHours(defaultLunchHours);
  }, []);

  // Operations
  const validateConfiguration = useCallback((): ValidationResult => {
    return validateOfficeHoursConfiguration(
      officeHoursInput,
      observesLunchHours,
      lunchHours
    );
  }, [officeHoursInput, observesLunchHours, lunchHours]);

  const saveOfficeHours = useCallback(async (
    practiceId: string,
    existingHours: OfficeHour[] = [],
    isEditMode: boolean = false
  ): Promise<OfficeHoursResult> => {
    setIsSubmitting(true);
    setLastResult(null);

    try {
      const config: OfficeHoursConfig = {
        officeHoursInput,
        observesLunchHours,
        lunchHours
      };

      const result = await managePracticeOfficeHours(
        practiceId,
        config,
        existingHours,
        isEditMode
      );

      setLastResult(result);
      return result;

    } catch (error) {
      const errorResult: OfficeHoursResult = {
        success: false,
        errors: [
          {
            field: 'officeHours',
            message: `Unexpected error: ${
              error instanceof Error ? error.message : 'Unknown error'
            }`,
            severity: 'error',
          } as ValidationError,
        ],
        warnings: [],
      };
      setLastResult(errorResult);
      return errorResult;
    } finally {
      setIsSubmitting(false);
    }
  }, [officeHoursInput, observesLunchHours, lunchHours]);

  return {
    // State
    officeHoursInput,
    observesLunchHours,
    lunchHours,
    validation,
    isSubmitting,
    lastResult,

    // Actions
    setOfficeHoursInput,
    setObservesLunchHours,
    setLunchHours,
    updateDayHours,
    toggleDayClosed,
    resetToDefaults,

    // Operations
    saveOfficeHours,
    validateConfiguration,

    // Computed
    hasChanges,
    isValid,
    errorMessages,
    warningMessages
  };
};
